import { Button, FormControlLabel, Grid, Switch } from '@mui/material';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { PlatformVersionPolicy, Study } from 'interfaces/study';
import { concat, filter, map } from 'lodash';
import React, { useMemo, useState } from 'react';
import { usePlatformDockerImages } from 'utils/queryHooks/usePlatformDockerImages';

const DEV_IMAGE_INTERNAL_NAME = '---dev---';
const LAB_DEFAULT_POLICY_INTERNAL_NAME = '---lab-default---';

export const DEFAULT_PLATFORM_VERSION_POLICY: PlatformVersionPolicy = 'locked';

export const PLATFORM_VERSION_POLICY_LABELS: Record<PlatformVersionPolicy, string> = {
  locked: 'Lock to the selected version',
  latest: 'Always use the latest stable version',
};

export const PlatformVersionPolicySelect: React.FC<{
  value: PlatformVersionPolicy | undefined;
  onChange: (value: PlatformVersionPolicy | undefined) => void;
  // When set, the select offers following the lab default instead of a study-level policy.
  labDefaultPolicy?: PlatformVersionPolicy;
  disabled?: boolean;
  helperText?: string;
}> = ({ value, onChange, labDefaultPolicy, disabled = false, helperText }) => {
  const canFollowLabDefault = labDefaultPolicy !== undefined;
  const policyOptions = map(PLATFORM_VERSION_POLICY_LABELS, (text, policy) => ({ value: policy, text }));
  const options = canFollowLabDefault
    ? [
        {
          value: LAB_DEFAULT_POLICY_INTERNAL_NAME,
          text: `Use lab default (${PLATFORM_VERSION_POLICY_LABELS[labDefaultPolicy]})`,
        },
        ...policyOptions,
      ]
    : policyOptions;
  const selectedValue =
    value ?? (canFollowLabDefault ? LAB_DEFAULT_POLICY_INTERNAL_NAME : DEFAULT_PLATFORM_VERSION_POLICY);

  return (
    <Grid item sx={{ minWidth: 280, maxWidth: 400 }}>
      <LabelledDropdown
        label="Platform Version policy"
        options={options}
        value={selectedValue}
        onOptionSelected={(optionValue) =>
          onChange(
            optionValue === LAB_DEFAULT_POLICY_INTERNAL_NAME ? undefined : (optionValue as PlatformVersionPolicy)
          )
        }
        disabled={disabled}
        helperText={helperText}
        dataTestId="platform-version-policy-select"
      />
    </Grid>
  );
};

// Stable platform image versions have no suffix after the patch number, or the "-full" suffix.
// Anything else (e.g. "-rc1", "_rc2-full", "_yuval1-full") is considered experimental.
const STABLE_PLATFORM_IMAGE_SUFFIXES = new Set(['', '-full']);
const PLATFORM_IMAGE_VERSION_REGEX = /^(.*)(\d+)\.(\d+)\.(\d+)(.*)$/;

// Docker images are stored as full registry URLs, e.g. "052019316843.dkr.ecr.us-east-2.amazonaws.com/algo:git-v1.13.15-full".
// Only the tag after the last colon (the version) is meaningful to users, and the leading "git-" is just noise.
const GIT_PREFIX_REGEX = /^git-/;

export const getDockerImageDisplayVersion = (dockerImage: string): string => {
  const lastColonIndex = dockerImage.lastIndexOf(':');
  const tag = lastColonIndex === -1 ? dockerImage : dockerImage.slice(lastColonIndex + 1);
  return tag.replace(GIT_PREFIX_REGEX, '');
};

const isExperimentalPlatformImage = (displayVersion: string): boolean => {
  const match = PLATFORM_IMAGE_VERSION_REGEX.exec(displayVersion);
  if (!match) {
    return false;
  }

  return !STABLE_PLATFORM_IMAGE_SUFFIXES.has(match[5]);
};

export const PlatformImageSelect: React.FC<
  React.PropsWithChildren<{
    updatedSettings: Study['platformSettings'];
    setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['platformSettings']>>;
    disabled?: boolean;
    helperText?: string;
    includeDevOption?: boolean;
  }>
> = ({ updatedSettings, setUpdatedSettings, disabled = false, helperText, includeDevOption = true }) => {
  const { images: platformImages, latestVersion, isLoading: isLoadingPlatformImages } = usePlatformDockerImages();
  const [showExperimentalVersions, setShowExperimentalVersions] = useState(false);
  const currentDockerImage = updatedSettings?.dockerImage;
  const isLatestVersionAssigned = Boolean(latestVersion) && currentDockerImage === latestVersion;

  const platformImageOptions = useMemo(
    () =>
      map(
        filter(
          platformImages,
          (image) =>
            showExperimentalVersions ||
            image === currentDockerImage ||
            !isExperimentalPlatformImage(getDockerImageDisplayVersion(image))
        ),
        (image) => {
          const displayVersion = getDockerImageDisplayVersion(image);
          return image === latestVersion
            ? { value: image, text: `${displayVersion} (recommended)` }
            : { value: image, text: displayVersion };
        }
      ),
    [currentDockerImage, latestVersion, platformImages, showExperimentalVersions]
  );

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ minWidth: 280, maxWidth: 400 }}>
            <LabelledDropdown
              label="Platform Version"
              options={
                includeDevOption
                  ? concat(platformImageOptions, { value: DEV_IMAGE_INTERNAL_NAME, text: 'dev' })
                  : platformImageOptions
              }
              value={updatedSettings?.dockerImage ?? DEV_IMAGE_INTERNAL_NAME}
              onOptionSelected={(optionValue) => {
                setUpdatedSettings((prevSettings) => ({
                  ...prevSettings,
                  dockerImage: optionValue !== DEV_IMAGE_INTERNAL_NAME ? optionValue : undefined,
                }));
              }}
              disabled={disabled}
              helperText={helperText}
              loading={isLoadingPlatformImages}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={showExperimentalVersions}
                  onChange={(event) => setShowExperimentalVersions(event.target.checked)}
                  disabled={disabled}
                  data-testid="show-experimental-platform-versions-toggle"
                />
              }
              label="Show experimental versions"
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          data-testid="assign-latest-platform-version"
          disabled={disabled || isLoadingPlatformImages || !latestVersion || isLatestVersionAssigned}
          onClick={() => {
            if (!latestVersion) {
              return;
            }

            setUpdatedSettings((prevSettings) => ({
              ...prevSettings,
              dockerImage: latestVersion,
            }));
          }}
        >
          Assign latest version
        </Button>
      </Grid>
    </Grid>
  );
};
